import { Fragment, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { IconType } from 'react-icons';

import { createStepHighlightCycle, killStepHighlightTweens } from '../../lib/gsapHighlight';
import SbrIcon from './SbrIcon';

export type RetingentProcessStep = {
  title: string;
  body: string;
  Icon: IconType;
};

type Props = {
  steps: RetingentProcessStep[];
};

const RING_SHADOW = '0 8px 24px rgba(0, 194, 171, 0.22)';
const RING_ACTIVE_SHADOW =
  '0 0 0 2px rgba(0, 194, 171, 0.55), 0 0 24px rgba(0, 194, 171, 0.75), 0 8px 24px rgba(43, 147, 240, 0.35)';

export default function RetingentProcessFlow({ steps }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      if (!rootRef.current) return;

      const stepEls = rootRef.current.querySelectorAll<HTMLElement>('.rg-process__step');
      const connectorEls = rootRef.current.querySelectorAll<HTMLElement>('.rg-process__connector');
      const ringEls = rootRef.current.querySelectorAll<HTMLElement>('.rg-process__icon-ring');
      if (!stepEls.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let cycleTween: gsap.core.Timeline | null = null;

      const highlightStep = (index: number) => {
        killStepHighlightTweens(stepEls, connectorEls, ringEls);

        stepEls.forEach((step, j) => {
          const ring = step.querySelector<HTMLElement>('.rg-process__icon-ring');
          const isActive = j === index;

          if (ring) {
            if (isActive) {
              gsap.to(ring, {
                scale: 1.12,
                boxShadow: RING_ACTIVE_SHADOW,
                borderColor: 'rgba(0, 194, 171, 0.85)',
                duration: 0.35,
                ease: 'back.out(1.6)',
                overwrite: true,
              });
            } else {
              gsap.set(ring, {
                scale: 1,
                boxShadow: RING_SHADOW,
                borderColor: 'rgba(255, 255, 255, 0.35)',
              });
            }
          }

          if (isActive) {
            gsap.to(step, { y: -4, duration: 0.35, ease: 'power2.out', overwrite: true });
          } else {
            gsap.set(step, { y: 0 });
          }
        });

        connectorEls.forEach((connector, j) => {
          const isLeading = j === index - 1;
          if (isLeading) {
            gsap.to(connector, {
              opacity: 1,
              borderColor: 'rgba(0, 194, 171, 0.95)',
              duration: 0.35,
              overwrite: true,
            });
          } else {
            gsap.set(connector, {
              opacity: 0.35,
              borderColor: 'rgba(0, 194, 171, 0.45)',
            });
          }
        });
      };

      const startCycle = () => {
        cycleTween?.kill();
        highlightStep(0);
        if (reducedMotion) return;
        cycleTween = createStepHighlightCycle(steps.length, highlightStep);
      };

      const cleanup = () => {
        cycleTween?.kill();
        cycleTween = null;
      };

      if (reducedMotion) {
        gsap.set(stepEls, { opacity: 1, y: 0, clearProps: 'opacity,transform' });
        gsap.set(connectorEls, { opacity: 1, scaleX: 1, clearProps: 'opacity,transform' });
        startCycle();
        return cleanup;
      }

      gsap.set(stepEls, { opacity: 0, y: 28 });
      gsap.set(connectorEls, { opacity: 0, scaleX: 0, transformOrigin: 'left center' });

      const reveal = gsap.timeline({
        paused: true,
        onComplete: startCycle,
      });

      stepEls.forEach((step, i) => {
        const offset = i * 0.18;
        reveal.to(step, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, offset);

        const connector = connectorEls[i];
        if (connector) {
          reveal.to(connector, { opacity: 1, scaleX: 1, duration: 0.45, ease: 'power2.out' }, offset + 0.12);
        }
      });

      let revealStarted = false;
      const playReveal = () => {
        if (revealStarted) return;
        revealStarted = true;
        reveal.play();
      };

      const scrollTrigger = ScrollTrigger.create({
        trigger: rootRef.current,
        start: 'top 82%',
        onEnter: playReveal,
        onEnterBack: playReveal,
        invalidateOnRefresh: true,
      });

      if (ScrollTrigger.isInViewport(rootRef.current)) {
        playReveal();
      }

      const refreshId = requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });

      return () => {
        cancelAnimationFrame(refreshId);
        scrollTrigger.kill();
        reveal.kill();
        cleanup();
      };
    },
    { scope: rootRef, dependencies: [steps.length] },
  );

  return (
    <div ref={rootRef} className="rg-process__track px-lg-2">
      <div className="rg-process__steps">
        {steps.map((step, index) => (
          <Fragment key={step.title}>
            <article className="rg-process__step">
              <div className="rg-process__icon-ring" aria-hidden>
                <SbrIcon icon={step.Icon} size={24} />
              </div>
              <div className={`rg-process__copy ${index > 0 ? 'rg-process__copy--divided' : ''}`}>
                <h3 className="rg-process__step-title">{step.title}</h3>
                <p className="rg-process__step-body mb-0">{step.body}</p>
              </div>
            </article>
            {index < steps.length - 1 && <div className="rg-process__connector" aria-hidden />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
